import { Button } from '@casumo/dojo-react';
import { getName } from 'i18n-iso-countries';
import { memo } from 'react';
import useMarketInfo from '../../hooks/useMarketInfo.tsx';
import useNavigateTo from '../../hooks/useNavigateTo.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TEST_ID } from '../../utils/constants';
import { countryCodeAliases, countryMarketInfoMapper } from '../../utils/countries.ts';

type TCountrySelectorButtonProps = {
  handleNext: ({ gtmPayload, redirectTo }: {
    gtmPayload?: Record<string, string>;
    redirectTo?: string;
    canProceed?: boolean;
  }) => void;
  gtmPayload?: Record<string, string>;
  label?: string;
}

const CountrySelectorButton = ({
  label,
  gtmPayload,
  handleNext,
}: TCountrySelectorButtonProps) => {
  const { formData, setMetadata, metadata } = useRegistrationStore();
  const { market } = useMarketInfo();
  const { navigateTo } = useNavigateTo();

  const getCountryName = (countryCode: string, locale = 'en') => {
    const useAliasName = countryCodeAliases.includes(countryCode);

    return useAliasName
      ? getName(countryCode, locale, { select: 'alias' })
      : getName(countryCode, locale);
  };

  const handleClick = () => {
    const selectedMarket = countryMarketInfoMapper
      .find((country) => country.countryCode === formData?.countryOfResidence)?.market;

    //TODO: Remove after migration to v2
    // Special case for Ontario, Canada redirection to a regily registration flow
    if (selectedMarket === 'en-ca' && metadata?.selectedProvinceCode === 'ON') {
      window.location.href = '/on/ontario-closure'

      return;
    }

    if (!selectedMarket || market === selectedMarket) {
      handleNext({ gtmPayload, redirectTo: '1', canProceed: true });

      return;
    }

    const oldData = JSON.parse(localStorage.getItem(`data_${selectedMarket}`) || '{}');

    /* Save the selected country in the localStorage for the selected market. */
    localStorage.setItem(`data_${selectedMarket}`, JSON.stringify({
      ...oldData,
      countryOfResidence: formData?.countryOfResidence,
      phonePrefix:        oldData?.phonePrefix,
      nationality:        oldData?.nationality,
      countryOfBirth:     oldData?.countryOfBirth,
    }))

    //TODO: Remove this after migration to v2
    localStorage.setItem('data', JSON.stringify({
      routeName:                     'WELCOME_OFFER',
      countryOfResidence:            getCountryName(formData?.countryOfResidence),
      countryOfResidenceCountryCode: formData?.countryOfResidence,
      countryRedirect:               true,
      selectedProvince:              formData?.countryOfResidence === 'CA' ? metadata?.selectedProvince : null,
      selectedProvinceCode:          formData?.countryOfResidence === 'CA' ? metadata?.selectedProvinceCode : null,
    }));

    setMetadata({ hasRedirected: true });

    // Get the progress step from the localStorage for the selected market.
    const selectedMarketProgressStep = localStorage.getItem(`step_${selectedMarket}`);
    if (selectedMarketProgressStep) {
      navigateTo(selectedMarket, +selectedMarketProgressStep);

      return;
    }

    // If it doesn't exist, set it to 2, skipping the first step.
    localStorage.setItem(`step_${selectedMarket}`, '2');
    navigateTo(selectedMarket, 2);
  }

  return (
    <Button
      width="full"
      className="focus:outline-purpleblue"
      onClick={handleClick}
      data-testid={TEST_ID.BUTTON_PRIMARY}
      disabled={formData?.countryOfResidence !== 'CA'
        ? !formData?.countryOfResidence
        : !formData?.province || !formData?.countryOfResidence
      }
    >
      {label}
    </Button>)
};

const Memoized = memo(CountrySelectorButton);
Memoized.displayName = 'LoginButton';

export default Memoized;
